import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import JSZip from "jszip";
import {
  Archive,
  ArrowLeft,
  Download,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
  Presentation,
  Save,
  Share2,
} from "lucide-react";
import {
  compressPdf,
  editPdfPages,
  extractPdfText,
  loadPdf,
  pdfBufferToBlob,
  pdfToDocxBlob,
  pdfToImages,
  renderPage,
} from "../lib/pdf.js";
import {
  docxToHtml,
  htmlToDocxBlob,
  parsePptx,
  parseWorkbook,
  pptxToBlob,
  reZipCompress,
  sheetToCsv,
  sheetsToJson,
  sheetsToXlsxBlob,
  textToDocxBlob,
} from "../lib/office.js";
import {
  compressImage,
  convertImage,
  defaultEdits,
  imagesToPdfBlob,
  isEdited,
  loadImageElement,
  renderEditedCanvas,
} from "../lib/image.js";
import { elementToPdfBlob, elementsToPdfBlob } from "../lib/render.js";
import { qrDataUrl, shareCurrentFile, shareLinkFor } from "../lib/share.js";
import {
  cloudHealth,
  cloudShareLink,
  createCloudShare,
  uploadCloudFile,
} from "../lib/cloud.js";
import { getRecord, saveRecord } from "../lib/db.js";
import {
  baseName,
  blobToDataURL,
  blobToText,
  canvasToBlob,
  downloadBlob,
  extname,
  formatBytes,
  formatDate,
  kindLabel,
  stripHtml,
  withExt,
} from "../lib/utils.js";
import PdfPreview from "./PdfPreview.jsx";
import DocxEditor from "./DocxEditor.jsx";
import XlsxEditor from "./XlsxEditor.jsx";
import PptxEditor from "./PptxEditor.jsx";
import ImagePreview from "./ImagePreview.jsx";
import TextEditor from "./TextEditor.jsx";
import ToolsPanel from "./ToolsPanel.jsx";

const TARGETS = {
  pdf: [
    { id: "pdf2png", label: "转 PNG 图片", desc: "逐页导出为高清图片", options: ["format", "scale", "pages"] },
    { id: "pdf2jpg", label: "转 JPG 图片", desc: "高质量 JPEG 导出", options: ["format", "scale", "pages", "quality"] },
    { id: "pdf2docx", label: "转 Word 文档", desc: "提取文字生成 DOCX" },
    { id: "pdf2txt", label: "转纯文本", desc: "提取全部页面文字" },
  ],
  docx: [
    { id: "docx2pdf", label: "转 PDF", desc: "保留排版打印导出" },
    { id: "docx2txt", label: "转 TXT", desc: "去除格式导出文本" },
    { id: "docx2html", label: "转 HTML", desc: "便于再次导入或发布" },
    { id: "docx2docx", label: "重新生成 DOCX", desc: "保存当前编辑内容" },
  ],
  xlsx: [
    { id: "xlsx2csv", label: "转 CSV", desc: "导出当前工作表" },
    { id: "xlsx2json", label: "转 JSON", desc: "导出全部工作表" },
    { id: "xlsx2pdf", label: "转 PDF", desc: "按表格排版导出" },
    { id: "xlsx2xlsx", label: "重新生成 XLSX", desc: "保存表格编辑" },
  ],
  sheet: [
    { id: "xlsx2csv", label: "转 CSV", desc: "导出当前工作表" },
    { id: "xlsx2json", label: "转 JSON", desc: "导出全部工作表" },
    { id: "xlsx2pdf", label: "转 PDF", desc: "按表格排版导出" },
    { id: "xlsx2xlsx", label: "重新生成 XLSX", desc: "保存表格编辑" },
  ],
  pptx: [
    { id: "pptx2pdf", label: "转 PDF", desc: "逐页导出幻灯片" },
    { id: "pptx2txt", label: "转 TXT", desc: "提取所有文字" },
    { id: "pptx2pptx", label: "重新生成 PPTX", desc: "保存文字编辑" },
  ],
  image: [
    { id: "img2png", label: "转 PNG", desc: "无损格式导出", options: ["format"] },
    { id: "img2jpg", label: "转 JPG", desc: "可调质量", options: ["format", "quality"] },
    { id: "img2webp", label: "转 WebP", desc: "网页常用格式", options: ["format", "quality"] },
    { id: "img2pdf", label: "转 PDF", desc: "图片合成 PDF", options: ["format"] },
  ],
  text: [
    { id: "txt2pdf", label: "转 PDF", desc: "按 A4 排版导出" },
    { id: "txt2docx", label: "转 Word", desc: "按段落生成文档" },
  ],
};

export default function Workspace({ fileId, initialTool, onBack, onToast }) {
  const [record, setRecord] = useState(null);
  const [currentName, setCurrentName] = useState("");
  const [busy, setBusy] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [toolTab, setToolTab] = useState(initialTool === "compress" ? "compress" : initialTool === "share" ? "share" : "convert");

  const [pdfData, setPdfData] = useState(null);
  const [pageOps, setPageOps] = useState([]);
  const [selectedPage, setSelectedPage] = useState(0);
  const [pdfEdited, setPdfEdited] = useState(false);

  const [imageSrc, setImageSrc] = useState("");
  const [imageEdits, setImageEdits] = useState(defaultEdits());
  const [imageEditedSrc, setImageEditedSrc] = useState("");

  const [docxHtml, setDocxHtml] = useState("");
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [selectedCell, setSelectedCell] = useState(null);
  const [pptx, setPptx] = useState(null);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const [text, setText] = useState("");

  const [outputs, setOutputs] = useState([]);
  const [compressed, setCompressed] = useState(null);
  const [qrUrl, setQrUrl] = useState("");
  const [cloudReady, setCloudReady] = useState(null);
  const [cloudShare, setCloudShare] = useState(null);
  const [cloudQrUrl, setCloudQrUrl] = useState("");
  const [cloudBusy, setCloudBusy] = useState(false);
  const [cloudExpiresDays, setCloudExpiresDays] = useState(7);

  const [activeTarget, setActiveTarget] = useState("");
  const [convertOpts, setConvertOpts] = useState({ format: "png", scale: 2, quality: 0.92, allPages: true });
  const [compressOpts, setCompressOpts] = useState({ maxWidth: 1600, quality: 0.8, scale: 1.4 });

  const docxEditorRef = useRef(null);
  const sheetTableRef = useRef(null);
  const pptxWrapRef = useRef(null);
  const pptUrlsRef = useRef([]);

  const kind = record?.kind;
  const targets = useMemo(() => (kind ? TARGETS[kind] || [] : []), [kind]);

  useEffect(() => {
    if (targets.length && !targets.some((t) => t.id === activeTarget)) {
      setActiveTarget(targets[0].id);
    }
  }, [targets, activeTarget]);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      const rec = await getRecord(fileId);
      if (cancelled) return;
      if (!rec) {
        onToast("文件不存在或已被删除", "error");
        onBack();
        return;
      }
      setRecord(rec);
      setCurrentName(rec.name);
      setBusy({ label: "正在解析文件..." });
      try {
        if (rec.kind === "pdf") {
          const buffer = await rec.blob.arrayBuffer();
          const pdf = await loadPdf(buffer);
          setPdfData({ pdf, buffer, thumbs: [] });
          setPageOps(Array.from({ length: pdf.numPages }, (_, i) => ({ orig: i, rot: 0 })));
          const thumbs = [];
          for (let i = 1; i <= pdf.numPages; i += 1) {
            const canvas = await renderPage(pdf, i, 0.34);
            thumbs.push({ orig: i - 1, canvas });
            if (!cancelled) setBusy({ label: `正在生成页面预览 ${i}/${pdf.numPages}` });
          }
          if (!cancelled) setPdfData((d) => (d ? { ...d, thumbs } : d));
        } else if (rec.kind === "image") {
          const src = await blobToDataURL(rec.blob);
          setImageSrc(src);
          setImageEditedSrc(src);
          setImageEdits(defaultEdits());
        } else if (rec.kind === "docx") {
          const { html } = await docxToHtml(await rec.blob.arrayBuffer());
          setDocxHtml(html);
        } else if (rec.kind === "xlsx") {
          const { sheets: parsed } = parseWorkbook(await rec.blob.arrayBuffer());
          setSheets(parsed);
        } else if (rec.kind === "sheet") {
          const raw = await blobToText(rec.blob);
          const { sheets: parsed } = parseWorkbook(new TextEncoder().encode(raw).buffer);
          setSheets(parsed);
        } else if (rec.kind === "pptx") {
          const parsed = await parsePptx(await rec.blob.arrayBuffer());
          pptUrlsRef.current.push(...parsed.slides.flatMap((s) => s.imageUrls));
          setPptx(parsed);
        } else if (rec.kind === "text") {
          setText(await blobToText(rec.blob));
        } else {
          throw new Error("暂不支持该文件类型");
        }
      } catch (err) {
        onToast(`打开文件失败：${err.message}`, "error");
        if (!cancelled) onBack();
        return;
      }
      if (!cancelled) setBusy(null);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, [fileId, onBack, onToast]);

  useEffect(() => {
    return () => {
      pptUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (!imageSrc || !imageEdits) return;
    let cancelled = false;
    if (!isEdited(imageEdits)) {
      setImageEditedSrc(imageSrc);
      return undefined;
    }
    loadImageElement(imageSrc)
      .then((img) => renderEditedCanvas(img, imageEdits))
      .then((canvas) => canvasToBlob(canvas, "image/png"))
      .then(blobToDataURL)
      .then((url) => {
        if (!cancelled) setImageEditedSrc(url);
      })
      .catch(() => setImageEditedSrc(imageSrc));
    return () => {
      cancelled = true;
    };
  }, [imageSrc, imageEdits]);

  const shareLink = useMemo(() => (record ? shareLinkFor(record.id) : ""), [record]);

  useEffect(() => {
    let cancelled = false;
    cloudHealth().then((ok) => {
      if (!cancelled) setCloudReady(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!record || toolTab !== "share") return;
    let cancelled = false;
    setQrUrl("");
    qrDataUrl(shareLink)
      .then((url) => {
        if (!cancelled) setQrUrl(url);
      })
      .catch(() => setQrUrl(""));
    return () => {
      cancelled = true;
    };
  }, [record, toolTab, shareLink]);

  const buildCurrentBlob = useCallback(async () => {
    if (!record) return null;
    const blob = record.blob;
    if (kind === "pdf" && pdfEdited && pdfData) {
      const out = await editPdfPages(pdfData.buffer, {
        order: pageOps.map((op) => op.orig),
        rotations: Object.fromEntries(pageOps.map((op) => [op.orig, op.rot])),
        deleted: new Set(),
      });
      return pdfBufferToBlob(out);
    }
    if (kind === "image" && imageEdits && isEdited(imageEdits)) {
      return convertImage(blob, { format: "png", edits: imageEdits });
    }
    if (kind === "docx" && dirty) return htmlToDocxBlob(docxHtml);
    if ((kind === "xlsx" || kind === "sheet") && dirty) return sheetsToXlsxBlob(sheets);
    if (kind === "pptx" && dirty && pptx) return pptxToBlob(pptx.zip, slidesToBlocks());
    if (kind === "text" && dirty) return new Blob([text], { type: "text/plain;charset=utf-8" });
    return blob;
  }, [record, kind, pdfEdited, pdfData, pageOps, imageEdits, dirty, docxHtml, sheets, pptx, text]);

  function slidesToBlocks() {
    return pptx?.slides.flatMap((s) => s.blocks) || [];
  }

  function addOutput(name, blob) {
    const item = { id: `${Date.now()}-${Math.random().toString(16).slice(2)}`, name, blob, size: blob.size };
    setOutputs((list) => [item, ...list].slice(0, 12));
    downloadBlob(blob, name);
    return item;
  }

  function downloadOutput(out) {
    downloadBlob(out.blob, out.name);
  }

  async function save() {
    if (!record) return;
    setBusy({ label: "正在保存..." });
    try {
      const blob = await buildCurrentBlob();
      const meta = await saveRecord(blob, { ...record, name: currentName || record.name });
      setRecord({ ...meta, blob });
      setDirty(false);
      setPdfEdited(false);
      onToast("已保存到本地文档库", "success");
    } catch (err) {
      onToast(`保存失败：${err.message}`, "error");
    } finally {
      setBusy(null);
    }
  }

  async function download() {
    if (!record) return;
    try {
      const blob = await buildCurrentBlob();
      downloadBlob(blob, currentName || record.name);
      onToast("已开始下载", "success");
    } catch (err) {
      onToast(`下载失败：${err.message}`, "error");
    }
  }

  async function runConversion(target) {
    if (!target || !record) return;
    setBusy({ label: `正在${target.label}...` });
    const name = currentName || record.name;
    try {
      const currentBlob = await buildCurrentBlob();
      if (target.id === "pdf2png" || target.id === "pdf2jpg") {
        const pdf = await loadPdf(await currentBlob.arrayBuffer());
        const format = target.id === "pdf2jpg" ? "jpg" : "png";
        const pages = convertOpts.allPages ? undefined : [pageOps[selectedPage]?.orig + 1 || 1];
        const images = await pdfToImages(pdf, {
          format,
          scale: convertOpts.scale,
          pages,
        });
        if (images.length === 1) {
          addOutput(withExt(name, format), images[0].blob);
        } else {
          const zip = new JSZip();
          images.forEach((img) =>
            zip.file(`page-${String(img.page).padStart(2, "0")}.${format}`, img.blob),
          );
          const zipped = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
          addOutput(withExt(name, "zip"), zipped);
        }
        onToast("PDF 转图片完成", "success");
      } else if (target.id === "pdf2docx") {
        const pdf = await loadPdf(await currentBlob.arrayBuffer());
        addOutput(withExt(name, "docx"), await pdfToDocxBlob(pdf));
        onToast("PDF 转 Word 完成", "success");
      } else if (target.id === "pdf2txt") {
        const pdf = await loadPdf(await currentBlob.arrayBuffer());
        const txt = await extractPdfText(pdf);
        addOutput(withExt(name, "txt"), new Blob([txt], { type: "text/plain;charset=utf-8" }));
        onToast("PDF 文本提取完成", "success");
      } else if (target.id === "docx2pdf") {
        await document.fonts?.ready;
        const blob = await elementToPdfBlob(docxEditorRef.current);
        addOutput(withExt(name, "pdf"), blob);
        onToast("Word 转 PDF 完成", "success");
      } else if (target.id === "docx2txt") {
        addOutput(withExt(name, "txt"), new Blob([stripHtml(docxHtml)], { type: "text/plain;charset=utf-8" }));
      } else if (target.id === "docx2html") {
        const html = `<!doctype html><html lang="zh-CN"><head><meta charset="utf-8"><title>${escapeHtml(name)}</title><style>body{font-family:system-ui,'PingFang SC','Microsoft YaHei',sans-serif;max-width:760px;margin:40px auto;padding:0 20px;line-height:1.75;color:#1c2333}</style></head><body>${docxHtml}</body></html>`;
        addOutput(withExt(name, "html"), new Blob([html], { type: "text/html;charset=utf-8" }));
      } else if (target.id === "docx2docx") {
        addOutput(withExt(name, "docx"), await htmlToDocxBlob(docxHtml));
      } else if (target.id === "xlsx2csv") {
        const csv = sheetToCsv(sheets[selectedSheet]?.data || []);
        addOutput(withExt(name, "csv"), new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
      } else if (target.id === "xlsx2json") {
        addOutput(withExt(name, "json"), new Blob([sheetsToJson(sheets)], { type: "application/json" }));
      } else if (target.id === "xlsx2pdf") {
        await document.fonts?.ready;
        addOutput(withExt(name, "pdf"), await elementToPdfBlob(sheetTableRef.current));
      } else if (target.id === "xlsx2xlsx") {
        addOutput(withExt(name, "xlsx"), sheetsToXlsxBlob(sheets));
      } else if (target.id === "pptx2pdf") {
        await document.fonts?.ready;
        const cards = Array.from(pptxWrapRef.current?.querySelectorAll(".ppt-slide-card") || []);
        addOutput(withExt(name, "pdf"), await elementsToPdfBlob(cards));
      } else if (target.id === "pptx2txt") {
        const txt = pptx.slides
          .map((s) => `第 ${s.number} 页\n${s.blocks.map((b) => b.text).join("\n")}`)
          .join("\n\n");
        addOutput(withExt(name, "txt"), new Blob([txt], { type: "text/plain;charset=utf-8" }));
      } else if (target.id === "pptx2pptx") {
        addOutput(withExt(name, "pptx"), await pptxToBlob(pptx.zip, slidesToBlocks()));
      } else if (target.id === "img2png" || target.id === "img2jpg" || target.id === "img2webp") {
        const format = target.id.slice(4);
        const blob = await convertImage(record.blob, {
          format,
          quality: convertOpts.quality,
          edits: imageEdits,
        });
        addOutput(withExt(name, format === "jpg" ? "jpg" : format), blob);
        onToast(`图片已转换为 ${format.toUpperCase()}`, "success");
      } else if (target.id === "img2pdf") {
        const current = await buildCurrentBlob();
        const pdf = await imagesToPdfBlob([{ blob: current }], {
          format: convertOpts.format === "png" ? "png" : "jpg",
        });
        addOutput(withExt(name, "pdf"), pdf);
      } else if (target.id === "txt2pdf") {
        const { jsPDF } = await import("jspdf");
        const pdf = new jsPDF({ unit: "pt", format: "a4" });
        const lines = pdf.splitTextToSize(text || " ", 500);
        pdf.text(lines, 40, 60);
        addOutput(withExt(name, "pdf"), pdf.output("blob"));
      } else if (target.id === "txt2docx") {
        addOutput(withExt(name, "docx"), await textToDocxBlob(text));
      }
      onToast(`转换完成：${target.label}`, "success");
    } catch (err) {
      onToast(`转换失败：${err.message}`, "error");
    } finally {
      setBusy(null);
    }
  }

  async function runCompress() {
    if (!record) return;
    setBusy({ label: "正在压缩..." });
    const name = currentName || record.name;
    try {
      let blob;
      if (kind === "pdf") {
        const current = await buildCurrentBlob();
        const out = await compressPdf(await current.arrayBuffer(), {
          quality: compressOpts.quality,
          scale: compressOpts.scale,
        });
        blob = pdfBufferToBlob(out);
      } else if (kind === "image") {
        blob = await compressImage(await buildCurrentBlob(), compressOpts);
      } else if (["docx", "xlsx", "pptx", "sheet"].includes(kind)) {
        blob = await reZipCompress(await buildCurrentBlob());
      } else {
        onToast("当前格式暂不支持压缩", "error");
        return;
      }
      const outName = kind === "image" ? withExt(name, "jpg") : withExt(name, extname(name));
      setCompressed({ name: outName, blob, size: blob.size });
      addOutput(outName, blob);
      onToast("压缩完成", "success");
    } catch (err) {
      onToast(`压缩失败：${err.message}`, "error");
    } finally {
      setBusy(null);
    }
  }

  async function copyShare() {
    try {
      await navigator.clipboard.writeText(shareLink);
      onToast("分享链接已复制", "success");
    } catch {
      onToast("复制失败，请手动选择链接", "error");
    }
  }

  async function downloadQr() {
    if (!qrUrl) return;
    const blob = await (await fetch(qrUrl)).blob();
    downloadBlob(blob, `二维码-${baseName(currentName || record.name)}.png`);
    onToast("二维码图片已下载", "success");
  }

  async function systemShare() {
    try {
      const current = await buildCurrentBlob();
      await shareCurrentFile({ ...record, blob: current, name: currentName || record.name }, shareLink);
      onToast("已调起系统分享", "success");
    } catch (err) {
      onToast(err.message || "系统分享失败", "error");
    }
  }

  async function runCloudShare() {
    if (!record) return;
    setCloudBusy(true);
    try {
      const blob = await buildCurrentBlob();
      const meta = await uploadCloudFile(blob, currentName || record.name);
      const share = await createCloudShare(meta.id, cloudExpiresDays);
      const link = cloudShareLink(share.token);
      setCloudShare({ token: share.token, link, fileId: meta.id });
      setCloudQrUrl(await qrDataUrl(link));
      onToast("已上传到云端并生成分享链接", "success");
    } catch (err) {
      onToast(err.message || "云端分享失败", "error");
    } finally {
      setCloudBusy(false);
    }
  }

  async function copyCloudShare() {
    if (!cloudShare) return;
    try {
      await navigator.clipboard.writeText(cloudShare.link);
      onToast("云端分享链接已复制", "success");
    } catch {
      onToast("复制失败，请手动选择链接", "error");
    }
  }

  async function downloadCloudQr() {
    if (!cloudQrUrl) return;
    const blob = await (await fetch(cloudQrUrl)).blob();
    downloadBlob(blob, `云端二维码-${baseName(currentName || record.name)}.png`);
    onToast("云端二维码已下载", "success");
  }

  // PDF 页面编辑
  const pdfEdit = {
    selectedLabel: pageOps.length ? `第 ${selectedPage + 1} 页（共 ${pageOps.length} 页）` : "无页面",
    rotateSelected() {
      if (!pageOps.length) return;
      setPageOps((ops) => ops.map((op, i) => (i === selectedPage ? { ...op, rot: (op.rot + 90) % 360 } : op)));
      setPdfEdited(true);
      setDirty(true);
    },
    deleteSelected() {
      if (!pageOps.length) return;
      setPageOps((ops) => {
        const next = ops.filter((_, i) => i !== selectedPage);
        setSelectedPage((p) => Math.max(0, Math.min(p, next.length - 1)));
        return next;
      });
      setPdfEdited(true);
      setDirty(true);
    },
    moveUp() {
      if (selectedPage <= 0) return;
      const target = selectedPage - 1;
      setPageOps((ops) => {
        const next = [...ops];
        [next[selectedPage], next[target]] = [next[target], next[selectedPage]];
        return next;
      });
      setSelectedPage(target);
      setPdfEdited(true);
      setDirty(true);
    },
    moveDown() {
      if (selectedPage >= pageOps.length - 1) return;
      const target = selectedPage + 1;
      setPageOps((ops) => {
        const next = [...ops];
        [next[selectedPage], next[target]] = [next[target], next[selectedPage]];
        return next;
      });
      setSelectedPage(target);
      setPdfEdited(true);
      setDirty(true);
    },
    resetPages() {
      setPageOps((ops) => ops.map((op) => ({ ...op, rot: 0 })).sort((a, b) => a.orig - b.orig));
      setSelectedPage(0);
      setPdfEdited(false);
      setDirty(false);
    },
  };

  // 表格编辑
  function updateSheets(next) {
    setSheets(next);
    setDirty(true);
  }
  function currentCols() {
    return Math.max(1, ...(sheets[selectedSheet]?.data.map((r) => r.length) || [1]));
  }
  function addRow() {
    if (selectedSheet == null || !sheets.length) return;
    const cols = currentCols();
    updateSheets(
      sheets.map((s, i) => (i === selectedSheet ? { ...s, data: [...s.data, Array(cols).fill("")] } : s)),
    );
  }
  function addColumn() {
    if (selectedSheet == null || !sheets.length) return;
    const cols = currentCols() + 1;
    updateSheets(
      sheets.map((s, i) =>
        i === selectedSheet
          ? { ...s, data: s.data.map((row) => [...row, ...Array(Math.max(0, cols - row.length)).fill("")]) }
          : s,
      ),
    );
  }
  function deleteRow() {
    if (!selectedCell || selectedSheet == null) {
      onToast("请先点击要删除的行", "info");
      return;
    }
    const r = selectedCell.r;
    updateSheets(sheets.map((s, i) => (i === selectedSheet ? { ...s, data: s.data.filter((_, x) => x !== r) } : s)));
    setSelectedCell(null);
  }
  function deleteColumn() {
    if (!selectedCell || selectedSheet == null) {
      onToast("请先点击要删除的列", "info");
      return;
    }
    const c = selectedCell.c;
    updateSheets(
      sheets.map((s, i) =>
        i === selectedSheet ? { ...s, data: s.data.map((row) => row.filter((_, x) => x !== c)) } : s,
      ),
    );
    setSelectedCell(null);
  }

  function markImageDirty(next) {
    setImageEdits(next);
    setDirty(isEdited(next));
  }

  if (!record) {
    return (
      <div className="workspace-loading">
        <Loader2 size={24} className="spin" />
        <p>正在打开文档...</p>
      </div>
    );
  }

  return (
    <div className="workspace">
      <header className="topbar">
        <button type="button" className="icon-btn topbar-back" onClick={onBack} title="返回首页">
          <ArrowLeft size={18} />
        </button>
        <div className="topbar-file">
          <input
            className="topbar-name"
            value={currentName}
            onChange={(e) => {
              setCurrentName(e.target.value);
              setDirty(true);
            }}
            aria-label="文件名"
          />
          <span className={`kind-badge kind-${kind}`}>{kindLabel(kind)}</span>
          {dirty && <span className="dirty-dot" title="有未保存修改" />}
        </div>
        <div className="topbar-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setToolTab("share")}>
            <Share2 size={15} />
            分享
          </button>
          <button type="button" className="btn" onClick={download}>
            <Download size={15} />
            下载
          </button>
          <button type="button" className="btn btn-primary" onClick={save}>
            <Save size={15} />
            保存
          </button>
        </div>
      </header>

      <div className="workspace-body">
        <div className="mobile-nav">
          {kind === "pdf" &&
            pageOps.map((op, i) => (
              <button
                type="button"
                key={`${op.orig}-${i}`}
                className={i === selectedPage ? "active" : ""}
                onClick={() => setSelectedPage(i)}
              >
                {i + 1}
              </button>
            ))}
          {(kind === "xlsx" || kind === "sheet") &&
            sheets.map((s, i) => (
              <button
                type="button"
                key={`${s.name}-${i}`}
                className={i === selectedSheet ? "active" : ""}
                onClick={() => setSelectedSheet(i)}
              >
                {s.name || `Sheet${i + 1}`}
              </button>
            ))}
          {kind === "pptx" &&
            pptx?.slides.map((s, i) => (
              <button
                type="button"
                key={s.number}
                className={i === selectedSlide ? "active" : ""}
                onClick={() => setSelectedSlide(i)}
              >
                幻灯片 {s.number}
              </button>
            ))}
        </div>
        <aside className="side-panel">
          <div className="side-section">
            <div className="side-title">
              <File size={15} />
              文件信息
            </div>
            <dl className="file-meta">
              <div>
                <dt>格式</dt>
                <dd>{kindLabel(kind)}</dd>
              </div>
              <div>
                <dt>大小</dt>
                <dd>{formatBytes(record.size)}</dd>
              </div>
              <div>
                <dt>更新时间</dt>
                <dd>{formatDate(record.updatedAt)}</dd>
              </div>
            </dl>
          </div>

          <div className="side-section grow">
            <div className="side-title">
              <KindNavIcon kind={kind} />
              {kind === "pdf" && `页面 (${pageOps.length})`}
              {(kind === "xlsx" || kind === "sheet") && `工作表 (${sheets.length})`}
              {kind === "pptx" && `幻灯片 (${pptx?.slides.length || 0})`}
              {!["pdf", "xlsx", "sheet", "pptx"].includes(kind) && "预览结构"}
            </div>

            {kind === "pdf" && (
              <div className="page-list">
                {pageOps.map((op, i) => {
                  const thumb = pdfData?.thumbs.find((t) => t.orig === op.orig);
                  return (
                    <button
                      type="button"
                      key={op.orig}
                      className={`page-thumb ${i === selectedPage ? "active" : ""}`}
                      onClick={() => setSelectedPage(i)}
                      title={`第 ${i + 1} 页`}
                    >
                      <div
                        className="thumb-canvas"
                        ref={(el) => {
                          if (el && thumb?.canvas && !el.querySelector("canvas")) el.appendChild(thumb.canvas);
                        }}
                      />
                      <span>{i + 1}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {(kind === "xlsx" || kind === "sheet") && (
              <div className="nav-list">
                {sheets.map((s, i) => (
                  <button
                    type="button"
                    key={`${s.name}-${i}`}
                    className={`nav-row ${i === selectedSheet ? "active" : ""}`}
                    onClick={() => setSelectedSheet(i)}
                  >
                    <FileSpreadsheet size={15} />
                    <span>{s.name || `Sheet${i + 1}`}</span>
                  </button>
                ))}
              </div>
            )}

            {kind === "pptx" && (
              <div className="nav-list">
                {pptx?.slides.map((s, i) => (
                  <button
                    type="button"
                    key={s.number}
                    className={`nav-row ${i === selectedSlide ? "active" : ""}`}
                    onClick={() => setSelectedSlide(i)}
                  >
                    <Presentation size={15} />
                    <span>幻灯片 {s.number}</span>
                  </button>
                ))}
              </div>
            )}

            {!["pdf", "xlsx", "sheet", "pptx"].includes(kind) && (
              <div className="nav-list">
                <div className="nav-row static">
                  <KindNavIcon kind={kind} />
                  <span>{kind === "image" ? "图片" : kind === "docx" ? "富文本" : kind === "text" ? "纯文本" : "文档"}</span>
                </div>
              </div>
            )}
          </div>

          <div className="side-section">
            <div className="side-title">
              <Archive size={15} />
              快捷操作
            </div>
            <div className="side-actions">
              <button type="button" className="side-action" onClick={save}>
                <Save size={15} />
                保存到文档库
              </button>
              <button type="button" className="side-action" onClick={download}>
                <Download size={15} />
                下载到设备
              </button>
              <button type="button" className="side-action" onClick={() => setToolTab("share")}>
                <Share2 size={15} />
                分享 / 二维码
              </button>
            </div>
          </div>
        </aside>

        <main className="preview-main">
          {busy && (
            <div className="busy-bar">
              <Loader2 size={15} className="spin" />
              {busy?.label || ""}
            </div>
          )}
          {kind === "pdf" && (
            <PdfPreview pdf={pdfData?.pdf} pageOps={pageOps} selectedIndex={selectedPage} busy={busy?.label} />
          )}
          {kind === "image" && <ImagePreview src={imageEditedSrc} name={currentName} kind={kind} />}
          {kind === "docx" && (
            <DocxEditor
              ref={docxEditorRef}
              html={docxHtml}
              onChange={(html) => {
                setDocxHtml(html);
                setDirty(true);
              }}
            />
          )}
          {(kind === "xlsx" || kind === "sheet") && (
            <XlsxEditor
              ref={sheetTableRef}
              sheets={sheets}
              selectedSheet={selectedSheet}
              selectedCell={selectedCell}
              onSelectSheet={setSelectedSheet}
              onSelectCell={setSelectedCell}
              onChange={updateSheets}
              onAddRow={addRow}
              onAddColumn={addColumn}
              onDeleteRow={deleteRow}
              onDeleteColumn={deleteColumn}
            />
          )}
          {kind === "pptx" && (
            <div ref={pptxWrapRef}>
              <PptxEditor
                slides={pptx?.slides || []}
                selectedSlide={selectedSlide}
                onSelect={setSelectedSlide}
                onEditBlock={(si, bi, value) => {
                  setPptx((p) => ({
                    ...p,
                    slides: p.slides.map((s, i) =>
                      i === si ? { ...s, blocks: s.blocks.map((b, j) => (j === bi ? { ...b, text: value } : b)) } : s,
                    ),
                  }));
                  setDirty(true);
                }}
              />
            </div>
          )}
          {kind === "text" && (
            <TextEditor
              text={text}
              onChange={(value) => {
                setText(value);
                setDirty(true);
              }}
            />
          )}
          {!kind && <div className="preview-empty">文档内容解析中</div>}
        </main>

        <ToolsPanel
          kind={kind}
          toolTab={toolTab}
          setToolTab={setToolTab}
          targets={targets}
          activeTarget={activeTarget}
          setActiveTarget={setActiveTarget}
          convertOpts={convertOpts}
          setConvertOpts={setConvertOpts}
          runConversion={runConversion}
          busy={busy?.label}
          record={record}
          currentName={currentName}
          setCurrentName={(v) => {
            setCurrentName(v);
            setDirty(true);
          }}
          compressed={compressed}
          runCompress={runCompress}
          compressOpts={compressOpts}
          setCompressOpts={setCompressOpts}
          qrUrl={qrUrl}
          shareLink={shareLink}
          copyShare={copyShare}
          downloadQr={downloadQr}
          systemShare={systemShare}
          cloudReady={cloudReady}
          cloudBusy={cloudBusy}
          cloudShare={cloudShare}
          cloudQrUrl={cloudQrUrl}
          cloudExpiresDays={cloudExpiresDays}
          setCloudExpiresDays={setCloudExpiresDays}
          runCloudShare={runCloudShare}
          copyCloudShare={copyCloudShare}
          downloadCloudQr={downloadCloudQr}
          pdfEdit={pdfEdit}
          imageEdits={imageEdits}
          setImageEdits={markImageDirty}
          resetImageEdits={() => markImageDirty(defaultEdits())}
          outputs={outputs}
          downloadOutput={downloadOutput}
        />
      </div>
    </div>
  );
}

function KindNavIcon({ kind }) {
  const map = {
    pdf: FileText,
    docx: File,
    xlsx: FileSpreadsheet,
    sheet: FileSpreadsheet,
    pptx: Presentation,
    image: FileImage,
    text: FileText,
  };
  const Icon = map[kind] || File;
  return <Icon size={15} />;
}

function escapeHtml(value = "") {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
